import React, { useRef, useEffect, useState } from "react"
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js"
import type { SeriesRow } from "~/api/snapshot_summaries"
import { formatCurrency } from "../utils"

Chart.register(CategoryScale, LinearScale, BarElement, Tooltip)

type Mode = "qoq" | "itd"

interface PnlBySeriesBarProps {
  series: SeriesRow[]
}

export default function PnlBySeriesBar({ series }: PnlBySeriesBarProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart<"bar"> | null>(null)
  const [mode, setMode] = useState<Mode>("qoq")

  const sorted = [...series]
    .map((s) => ({
      label: s.series,
      value:
        mode === "qoq" ? s.quarter_activity?.pl ?? 0 : s.performance?.pl ?? 0,
    }))
    .sort((a, b) => b.value - a.value)

  // minWidth ensures horizontal scroll kicks in on narrow viewports;
  // width: 100% lets the chart fill its container on desktop.
  const minChartWidth = Math.max(sorted.length * 20, 400)

  useEffect(() => {
    if (!canvasRef.current) return

    if (chartRef.current) {
      chartRef.current.destroy()
    }

    const ctx = canvasRef.current.getContext("2d")
    if (!ctx) return

    chartRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: sorted.map((r) => r.label),
        datasets: [
          {
            data: sorted.map((r) => r.value),
            backgroundColor: sorted.map((r) =>
              r.value >= 0 ? "#143764" : "#b03a2e",
            ),
            borderRadius: 2,
            maxBarThickness: 14,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => formatCurrency(ctx.raw as number),
            },
          },
        },
        scales: {
          x: {
            ticks: {
              maxRotation: 90,
              font: { size: 9 },
              autoSkip: true,
            },
            grid: { display: false },
          },
          y: {
            ticks: {
              callback: (v) => formatCurrency(v as number),
              font: { size: 10 },
            },
          },
        },
      },
    })

    const onResize = () => chartRef.current?.resize()
    window.addEventListener("resize", onResize)

    return () => {
      window.removeEventListener("resize", onResize)
      chartRef.current?.destroy()
      chartRef.current = null
    }
  }, [mode, series])

  const subtitle =
    mode === "qoq"
      ? `Quarter-over-quarter · ${series.length} series`
      : `Inception-to-date · ${series.length} series`

  return (
    <div>
      <div
        className="b-ps-card__header"
        style={{ borderBottom: "none", padding: "0 0 10px 0" }}
      >
        <div>
          <div className="b-ps-card__sub">{subtitle}</div>
        </div>
        <div className="b-ps-seg-toggle">
          {(["qoq", "itd"] as Mode[]).map((m) => (
            <button
              key={m}
              className={`b-ps-seg-btn${mode === m ? " active" : ""}`}
              onClick={() => setMode(m)}
            >
              {m === "qoq" ? "QoQ" : "ITD"}
            </button>
          ))}
        </div>
      </div>
      <div className="b-ps-bar-scroll">
        <div style={{ width: "100%", minWidth: minChartWidth, height: 340 }}>
          <canvas ref={canvasRef} />
        </div>
      </div>
    </div>
  )
}
